import { FC, useEffect, useState, type JSX } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import useDC from '@eneco/dc/useDC';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { NotificationBox, RadioGroup, RadioTile, TextLink } from '@eneco/sparky';
import {
  Electricity,
  ElectricityAndGas,
  ElectricityAndHeating,
  ElectricityGasHeating,
  Gas,
  Heating,
} from '@eneco/sparky/illustrations';
import { usePlaceholderContent } from '@sitecore/common';
import { DialogField, EnergyChoiceStepRendering } from '@sitecore/types/EnergyChoiceStep';
import { useMachine } from '@xstate/react';
import { Controller, useForm } from 'react-hook-form';

import { SmeFlowError } from '../../components';
import { FlowContextSme } from '../../context';
import { energyChoiceMachine } from '../../machines';
import { EnergyType } from '../../types';

interface FormFields {
  energyType?: EnergyType;
}

const generateInputElement = (value: EnergyType, label: string): JSX.Element => {
  let icon: JSX.Element;

  switch (value) {
    case EnergyType.Gas:
      icon = <Gas size="small" />;
      break;

    case EnergyType.ElectricityAndGas:
      icon = <ElectricityAndGas size="small" />;
      break;

    case EnergyType.ElectricityAndGasAndWarmth:
    case EnergyType.ElectricityAndGasAndWarmthAndTapWater:
      icon = <ElectricityGasHeating size="small" />;
      break;

    case EnergyType.ElectricityAndWarmth:
    case EnergyType.ElectricityAndWarmthAndTapWater:
    case EnergyType.ElectricityAndGeothermalHeating:
      icon = <ElectricityAndHeating size="small" />;
      break;

    case EnergyType.Warmth:
    case EnergyType.WarmthAndTapWater:
    case EnergyType.GeothermalHeating:
      icon = <Heating size="small" />;
      break;

    default:
      icon = <Electricity size="small" />;
  }

  return (
    <RadioTile key={value} value={value} icon={icon}>
      {label}
    </RadioTile>
  );
};

export const EnergyChoiceStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextSme>();
  const { send: sendToFlowMachine } = useFlowActorRef();

  const flowContext = useFlowSelector(state => state.context);
  const energyType = useFlowSelector(state => state.context.energyType);

  const [energyChoiceState, sendToEnergyChoiceMachine] = useMachine(energyChoiceMachine);

  const {
    context: { availableProducts = [] },
  } = energyChoiceState;

  const { EnergyChoiceStep: { fields: textualData } = {} } = usePlaceholderContent<{
    EnergyChoiceStep: EnergyChoiceStepRendering;
  }>();

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    setValue,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      // For this step we won't be using the "defaultValue" straight away since the earlier
      // selected "energyType" might not no longer be available due to address change
    },
  });

  const { businessUnit, label } = useDC();

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [isNotSupported, setIsNotSupported] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);

  const [shouldShowOfferAvailabilityNotification, setShouldShowOfferAvailabilityNotification] = useState(false);

  const watchEnergyType = watch('energyType');

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      type: 'GET_PRODUCT_TYPE_COMBINATIONS',
      config: {
        businessUnit,
        label,
      },
      values: { ...flowContext },
    };
    sendToEnergyChoiceMachine(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lifecycle componentDidMount
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful) {
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...getValues(),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  useEffect(() => {
    setShouldShowOfferAvailabilityNotification(
      !!(
        watchEnergyType &&
        watchEnergyType !== EnergyType.ElectricityAndGas &&
        watchEnergyType !== EnergyType.Warmth &&
        watchEnergyType !== EnergyType.WarmthAndTapWater &&
        !!textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content
      ),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchEnergyType]);

  useEffect(() => {
    if (!energyChoiceState.matches('IDLE')) {
      setIsLoading(energyChoiceState.matches('FETCHING'));
      setIsFailed(!energyChoiceState.matches('FETCHING') && !energyChoiceState.matches('SUCCESS'));
      setIsSuccessful(energyChoiceState.matches('SUCCESS'));

      setIsNotFound(energyChoiceState.matches('ERROR_NOT_FOUND'));
      setIsNotSupported(energyChoiceState.matches('ERROR_NOT_SUPPORTED'));

      if (energyChoiceState.matches('SUCCESS') && availableProducts.length > 0) {
        if (energyType) {
          setValue(
            'energyType',
            availableProducts.find((product: EnergyType) => product === energyType),
          );
        } else {
          setValue(
            'energyType',
            availableProducts.includes(EnergyType.ElectricityAndGas)
              ? EnergyType.ElectricityAndGas
              : availableProducts[0],
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [energyChoiceState]);

  // If "availableProducts" list consist of only "EnergyType.Electricity" product then
  // we should render "EnergyType.Electricity" specific title otherwise default title
  const title: string | undefined =
    availableProducts.length === 1 &&
    availableProducts.some((product: EnergyType) => product.includes(EnergyType.Electricity))
      ? textualData?.energyChoice?.electricityOnlyOptionText?.value
      : textualData?.content?.title?.value;

  // If "availableProducts" list includes "EnergyType.Warmth" then
  // we should render "EnergyType.Warmth" specific text otherwise default text
  const text: string | undefined = availableProducts.some((product: EnergyType) => product.includes(EnergyType.Warmth))
    ? textualData?.energyChoice?.warmthAvailableOptionText?.value
    : textualData?.content?.text?.value;

  // If "availableProducts" list includes "EnergyType.Warmth" then
  // we should render "EnergyType.Warmth" specific dialog
  const dialog: DialogField | undefined = availableProducts.some((product: EnergyType) =>
    product.includes(EnergyType.Warmth),
  )
    ? textualData?.content?.dialog
    : undefined;

  const getErrorNotification = () => {
    if (isNotFound) return textualData?.genericError?.errorNotFoundNotification;
    if (isNotSupported) return textualData?.genericError?.errorNotSupportedNotification;

    return textualData?.genericError?.errorNotification;
  };

  return (
    <Layout.Content variant="A" isLoading={isLoading} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <Header>
            <Header.Title>{title}</Header.Title>

            {text && (
              <Header.Text>
                <RichText html={text} />{' '}
                {dialog && (
                  <TrackedDialog title={dialog.value?.title} trigger={<TextLink>{dialog.value?.triggerText}</TextLink>}>
                    <RichText html={dialog?.value?.content} />
                  </TrackedDialog>
                )}
              </Header.Text>
            )}
          </Header>

          {textualData?.content?.notification?.value?.content && (
            <NotificationBox
              isAlert={false}
              variant={textualData?.content?.notification?.value?.variant}
              title={textualData?.content?.notification?.value?.title}
              text={<RichText html={textualData?.content?.notification?.value?.content} />}
            />
          )}

          <Controller
            control={control}
            rules={{ required: textualData?.energyChoice?.energyChoiceRadio?.value?.requiredMessage }}
            name="energyType"
            render={({ field: { onChange, value, name } }) => (
              <RadioGroup
                aria-labelledby="energyType"
                name={name}
                direction="column"
                value={value}
                onValueChange={onChange}
                error={errors['energyType']?.message}>
                {availableProducts.map((product: EnergyType) =>
                  generateInputElement(
                    product,
                    textualData?.energyChoice?.energyChoiceRadio?.value?.options?.find(
                      // since TapWater is not presented as a loose product we strip it out of the "product" name
                      option => option.name === product.replace('AndTapWater', ''),
                    )?.label ?? '',
                  ),
                )}
              </RadioGroup>
            )}
          />

          {shouldShowOfferAvailabilityNotification && (
            <TrackedNotificationBox
              isAlert={false}
              label={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title}
              title={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title ?? ''}
              text={
                <RichText html={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content} />
              }
              variant={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.variant}
            />
          )}

          <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
        </>
      )}

      {isFailed && (
        <SmeFlowError
          textualData={{
            notification: getErrorNotification(),
            buttonLabel: !isNotFound && !isNotSupported ? textualData?.genericError?.tryAgainButtonText : undefined,
          }}
        />
      )}
    </Layout.Content>
  );
};
